import { useRef, useCallback, useState, useEffect } from "react";
import "./index.css";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import HistoryIcon from "@mui/icons-material/History";
import { useMoviesStore } from "../../stores/useMoviesStore";
import { MovieItem } from "../../components/movie-item";
import { NoItemsFound } from "../../components/common/NoItemsFound";
import Loader from "../../components/common/Loader";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useSearchStore } from "../../stores/useSearchStore";
import { logCTAEvent } from "../../service/analytics";

const SearchScreen = () => {
  const {
    searchedMovies,
    fetchSearchedMovies,
    hasMoreSearched,
    searchQuery,
    setSearchQuery,
    clearSearch,
    isLoading,
  } = useMoviesStore();
  const { history, saveSearch, removeSearch } = useSearchStore();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const pageRef = useRef<number>(1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const executeSearch = useCallback(
    (queryToSearch?: string) => {
      const query = queryToSearch ?? searchQuery;
      if (!query.trim()) return;

      saveSearch(query);
      logCTAEvent("cta_search_submit", {
        search_query: query,
        results_count: searchedMovies.length,
      });
      fetchSearchedMovies({
        page: pageRef.current,
        query: query,
      });
      inputRef.current?.blur();
      if (pageRef.current === 1) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [fetchSearchedMovies, searchQuery, saveSearch, searchedMovies.length],
  );


  const handleQueryChange = () => {
    pageRef.current = 1;
    executeSearch();
    setIsInputFocused(false);
  };

  const handleClear = () => {
    clearSearch();
  };

  const loadMoreVideos = useCallback(() => {
    pageRef.current = pageRef.current + 1;
    executeSearch();
  }, [executeSearch]);

  const { lastElementRef } = useInfiniteScroll({
    isLoading,
    hasNextPage: hasMoreSearched,
    onLoadMore: loadMoreVideos,
  });

  const handleHistoryItemClick = (item: string) => {
    setSearchQuery(item);
    pageRef.current = 1;
    executeSearch(item);
    setIsInputFocused(false);
  };

  const handleRemoveHistoryItem = (e: React.MouseEvent, item: string) => {
    e.stopPropagation();
    e.preventDefault();
    removeSearch(item);
  };

  return (
    <div className="screen-container">
      <div className="sticky-search-container">
        <div
          style={{
            width: "90%",
            maxWidth: "600px",
            position: "relative",
          }}
        >
          <div className="search-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search Movies"
              className="search-input-field"
              value={searchQuery}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setTimeout(() => setIsInputFocused(false), 200)}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleQueryChange();
                }
              }}
            />
            {searchQuery && (
              <ClearIcon className="clear-icon-btn" onClick={handleClear} />
            )}
            <SearchIcon
              className="search-icon-btn"
              onClick={handleQueryChange}
            />
          </div>

          {/* Search History Dropdown */}
          {isInputFocused && history.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "65px",
                left: 0,
                right: 0,
                backgroundColor: "white",
                borderRadius: "10px",
                boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                zIndex: 100,
                border: "1px solid #eee",
                overflow: "hidden",
              }}
            >
              {history.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 15px",
                    cursor: "pointer",
                    borderBottom:
                      index === history.length - 1
                        ? "none"
                        : "1px solid #f0f0f0",
                  }}
                  onMouseDown={() => handleHistoryItemClick(item)}
                  className="history-item"
                >
                  <div
                    style={{ display: "flex", alignItems: "center", flex: 1 }}
                  >
                    <HistoryIcon
                      sx={{ fontSize: 20, color: "#999", marginRight: 1.5 }}
                    />
                    <span style={{ fontSize: "15px", color: "#333" }}>
                      {item}
                    </span>
                  </div>
                  <ClearIcon
                    sx={{
                      fontSize: 18,
                      color: "#ccc",
                      "&:hover": { color: "#666" },
                      padding: "4px",
                    }}
                    onMouseDown={(e) => handleRemoveHistoryItem(e, item)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isLoading && searchedMovies.length === 0 ? (
        <Loader />
      ) : searchedMovies.length === 0 ? (
        <NoItemsFound text="No movies found!" />
      ) : (
        <>
          <div className="grid-container">
            {searchedMovies.map((item) => (
              <MovieItem key={item.id} movie={item} />
            ))}
          </div>
          {hasMoreSearched && (
            <div ref={lastElementRef} style={{ height: "20px" }} />
          )}
        </>
      )}

      {isLoading && searchedMovies.length > 0 && <Loader />}
    </div>
  );
};

export default SearchScreen;
